import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogoIcon } from "../components/Brand";
import { useApp, API } from "../AppContext";
import { ArrowRight, Sun, Moon, Users } from "lucide-react";
import { useTheme } from "../hooks/useTheme";

export default function AcceptInvite() {
  const { lang, setLang } = useApp();
  const { theme, toggle: toggleTheme } = useTheme();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const isFr = lang === "fr";
  const token = params.get("token");
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [form, setForm] = useState({ password: "", name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [acceptLegal, setAcceptLegal] = useState(false);

  useEffect(() => {
    if (!token) { setErr(isFr ? "Aucun jeton d'invitation." : "No invitation token."); setLoadingInfo(false); return; }
    axios.get(`${API}/team/invites/${encodeURIComponent(token)}`)
      .then(({ data }) => { setInvite(data); setForm((f) => ({ ...f, name: data.name || "" })); })
      .catch((e) => setErr(e?.response?.data?.detail || (isFr ? "Invitation invalide" : "Invalid invitation")))
      .finally(() => setLoadingInfo(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!acceptLegal) return setErr(isFr ? "Vous devez accepter les conditions." : "You must accept the terms.");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/team/accept-invite`, { token, password: form.password, name: form.name });
      localStorage.setItem("progere_token", data.token);
      localStorage.setItem("progere_user", JSON.stringify(data.user));
      window.location.href = "/app";
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col relative"
      style={{ background: "radial-gradient(ellipse at top, rgba(212,175,55,0.08) 0%, transparent 55%), radial-gradient(ellipse at bottom, rgba(192,0,0,0.08) 0%, transparent 55%), #0B0B0B" }}>
      <div className="absolute top-5 right-5 flex items-center gap-2 z-10">
        <button onClick={() => setLang(isFr ? "en" : "fr")}
          className="text-xs px-3 py-1.5 rounded-full transition hover:brightness-110"
          style={{ background: "rgba(245,245,245,0.06)", border: "1px solid rgba(245,245,245,0.1)", color: "rgba(245,245,245,0.85)" }}
          data-testid="accept-lang-toggle">
          {isFr ? "EN" : "FR"}
        </button>
        <button onClick={toggleTheme}
          className="w-9 h-9 rounded-full flex items-center justify-center transition hover:brightness-110"
          style={{ background: "rgba(245,245,245,0.06)", border: "1px solid rgba(245,245,245,0.1)" }}
          aria-label="Toggle theme" data-testid="accept-theme-toggle">
          {theme === "dark" ? <Sun size={14} color="#D4AF37" /> : <Moon size={14} color="#0B0B0B" />}
        </button>
      </div>

      <div className="flex-1 w-full flex items-center justify-center px-4 sm:px-6 py-10">
        <div className="w-full max-w-md rounded-3xl p-8 sm:p-10"
          style={{
            background: "linear-gradient(180deg, rgba(24,24,24,0.9) 0%, rgba(11,11,11,0.95) 100%)",
            border: "1px solid rgba(212,175,55,0.18)",
            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.03)",
          }}
          data-testid="accept-invite">
          <div className="flex flex-col items-center mb-6">
            <div className="mb-4"><LogoIcon size="lg" /></div>
            <div className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-center leading-none">
              <span style={{ color: "#D4AF37" }}>PRO</span><span style={{ color: "#EF4444" }}>GÈRE</span>
              <span className="ml-2" style={{ color: "#F5F5F5" }}>DEVIS AI</span>
            </div>
          </div>

          {loadingInfo ? (
            <p className="text-center text-sm" style={{ color: "rgba(245,245,245,0.55)" }}>{isFr ? "Vérification..." : "Verifying..."}</p>
          ) : err && !invite ? (
            <div className="text-center py-6">
              <div className="text-sm px-3 py-3 rounded-xl mb-4" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }}>{err}</div>
              <Link to="/login" className="text-xs" style={{ color: "#D4AF37" }} data-testid="accept-back">← {isFr ? "Retour à la connexion" : "Back to sign-in"}</Link>
            </div>
          ) : (
            <>
              <div className="p-4 rounded-2xl mb-5" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.25)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <Users size={14} color="#D4AF37" />
                  <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#D4AF37" }}>
                    {isFr ? "Invitation d'équipe" : "Team invitation"}
                  </span>
                </div>
                <div className="text-sm" style={{ color: "#F5F5F5" }}>
                  {isFr ? "Vous êtes invité(e) à rejoindre" : "You're invited to join"}{" "}
                  <strong style={{ color: "#D4AF37" }}>{invite?.owner_company || invite?.owner_email}</strong>.
                </div>
                <div className="text-xs mt-1" style={{ color: "rgba(245,245,245,0.6)" }}>
                  {isFr ? "Compte à créer pour" : "Account for"} <span style={{ color: "#F5F5F5", fontWeight: 600 }}>{invite?.email}</span>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: "rgba(245,245,245,0.7)" }}>
                    {isFr ? "NOM COMPLET" : "FULL NAME"}
                  </label>
                  <input className="input-dark mt-2" required placeholder={isFr ? "Jean Tremblay" : "John Smith"}
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="accept-name" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: "rgba(245,245,245,0.7)" }}>
                    {isFr ? "MOT DE PASSE" : "PASSWORD"}
                  </label>
                  <input type="password" className="input-dark mt-2" required minLength={6} placeholder="••••••••"
                    value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="accept-password" />
                </div>
                <label className="flex items-start gap-2 text-xs cursor-pointer pt-1" style={{ color: "rgba(245,245,245,0.65)" }}>
                  <input type="checkbox" checked={acceptLegal} onChange={(e) => setAcceptLegal(e.target.checked)}
                    className="accent-[#D4AF37] mt-0.5" required data-testid="accept-legal" />
                  <span>
                    {isFr ? "J'accepte les " : "I accept the "}
                    <Link to="/conditions" target="_blank" style={{ color: "#D4AF37" }}>{isFr ? "conditions" : "terms"}</Link>
                    {isFr ? " et la " : " and the "}
                    <Link to="/confidentialite" target="_blank" style={{ color: "#D4AF37" }}>{isFr ? "confidentialité" : "privacy policy"}</Link>.
                  </span>
                </label>
                {err && (
                  <div className="text-sm px-3 py-2 rounded-xl" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }} data-testid="accept-error">{err}</div>
                )}
                <button type="submit" disabled={submitting} className="btn-gold w-full mt-2 disabled:opacity-50" data-testid="accept-submit">
                  {submitting ? "..." : <><ArrowRight size={16} /> {isFr ? "Rejoindre l'équipe" : "Join the team"}</>}
                </button>
              </form>
            </>
          )}

          <p className="mt-6 text-center text-[11px]" style={{ color: "rgba(245,245,245,0.4)" }}>
            PROGÈRE — {isFr ? "Propulsé par" : "Powered by"}{" "}
            <span style={{ color: "#D4AF37" }}>Groupe MJR Construction Inc.</span>
          </p>
        </div>
      </div>
    </div>
  );
}
